/* Interview Clozo — billing UI helpers for the billing pages (account.html now; the cancel and success pages may reuse
   them). Exposes window.clozoBillingUI. No page markup lives here: every helper takes the element it works on.

   RULES THIS FILE ENFORCES (owner rulings; CONTRACT R12–R16, R33, R34, R38)
   - confirm(): ONE native <dialog> per money action. The server's confirmText, termsText, buttonLabel and
     agreementText are rendered VERBATIM (textContent, never innerHTML); termsText sits directly under the pay button
     (D12.9: the terms are text next to the button). There is NO checkbox of any kind (D12.9, D12.20). Esc, x and the
     second control close it with no call; while a call runs nothing can close it and the button cannot be pressed
     twice; failures render inside the dialog; a 409 disclosure_changed re-renders the fresh texts and asks for the
     click again.
   - celebrate(): the plan-card celebration (D12.13) — headline + line only: no Member / Founding Member badge and no
     "You save" figure (D13.9). The confetti ("Ignition") is window.clozoIgnition from celebrate.js when a page
     declares it; without it the words still show, with a small check that respects reduced motion.
   - openAction(): checkout.js calls it on a 409 use_in_place_action; the account page registers the handler, so the
     dialog opens in place instead of navigating (returns true when it opened one). */
(function () {
  'use strict';
  if (window.clozoBillingUI && window.clozoBillingUI.version) return;

  var SESSION_KEY = 'interview_session';
  /* Contract C3-K1 (R2031, owner ruling D1): the checkout sentence also accepts the Affiliate Program Terms — every account is an
     affiliate from creation. The 20.3.0 sentence (AGREEMENT_PRE_K1) is still linked when an older server sends it. */
  var AGREEMENT = 'By continuing, you agree to our Terms of Service, Refund Policy and Affiliate Program Terms.';
  var AGREEMENT_PRE_K1 = 'By continuing, you agree to our Terms of Service and Refund Policy.';
  var actionHandler = null;

  /* ---------------- formatting (LEGAL-COPY §0.3: "$199.00", "October 9, 2026", short "Oct 9") ---------------- */
  function money(cents, currency) {
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(cents / 100);
    } catch (_) { return '$' + (cents / 100).toFixed(2); }
  }
  function per(interval) { return interval === 'year' ? 'year' : 'month'; }
  function day(ms) {
    if (typeof ms !== 'number' || !isFinite(ms)) return '';
    try { return new Date(ms).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
    catch (_) { return new Date(ms).toDateString(); }
  }
  function shortDay(ms) {
    if (typeof ms !== 'number' || !isFinite(ms)) return '';
    try { return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch (_) { return day(ms); }
  }
  /* Local date + time + zone, and the UTC twin (CONTRACT §0.8), for anything a person may need to quote back. */
  function stamp(ms) {
    if (typeof ms !== 'number' || !isFinite(ms)) return '';
    var d = new Date(ms);
    try {
      var local = d.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
      var utc = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
      return local + ' (' + utc + ' UTC)';
    } catch (_) { return d.toISOString(); }
  }
  function timeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { return ''; }
  }

  /* ---------------- environment ---------------- */
  /* D-FS1-1 (FS1 #8, FE-S1-001) → D-FS3-020 (lane w17c): does the Mac app take the browser hand-off
     (interview://signin#c=…, R36)? It does since wave 17b (fe-s3: IncomingURLRoute.signin → redeemAppLink → the session
     adopted), so the three [Open Interview Clozo] buttons (success, welcome, account) show. An app older than that build
     opens without signing in, so each button keeps its fallback line under it: "Didn't open? In the app, sign in with
     this email and your password." DEPLOY WITH the app release that carries R36 (owner-gated), never before it. */
  function appHandoff() { return true; }
  function uuid() {
    try { if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID(); } catch (_) {}
    var b = new Uint8Array(16);
    try { crypto.getRandomValues(b); } catch (_) { for (var i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256); }
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return (x < 16 ? '0' : '') + x.toString(16); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }
  /* macOS and not a touch device (an iPad reports "Macintosh" too). */
  function isMac() {
    var ua = navigator.userAgent || '';
    return /Macintosh|Mac OS X/.test(ua) && !(navigator.maxTouchPoints > 1);
  }
  function reducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
  }
  function token() {
    try {
      var s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
      if (!s || !s.token) return null;
      if (typeof s.expiresAtMillis === 'number' && s.expiresAtMillis > 0 && Date.now() >= s.expiresAtMillis) return null;
      return s.token;
    } catch (_) { return null; }
  }

  /* ---------------- first-party events (#39 / #40 / #32; site-runtime.js owns the queue, W6) ---------------- */
  function track(name, props) {
    try {
      var rt = window.__clozoRuntime;
      if (rt && typeof rt.track === 'function') rt.track(name, props || {});
    } catch (_) {}
  }

  /* ---------------- one authed JSON call; never throws ----------------
     Resolves {status, ok, data, network}. status 0 + network:true = no answer (offline, timeout). */
  function reportApi(area, path, method, r) {   // OBSERVABILITY §2.7 (site-runtime.js, W6); machine values only
    try {
      var rt = window.__clozoRuntime;
      if (!r || r.ok || !rt || typeof rt.reportFailure !== 'function') return;
      var d = r.data || {};
      rt.reportFailure(area, { path: path, method: method, status: r.status || 0, code: d.code || d.error,
        network: !!r.network, nonJson: !!r.nonJson, provider: d.provider, retryQueued: d.retryQueued });
    } catch (_) {}
    return r;
  }
  function api(method, path, body, opts) {
    opts = opts || {};
    var headers = { 'Content-Type': 'application/json' };
    var t = token();
    if (t) headers.Authorization = 'Bearer ' + t;
    var init = { method: method, headers: headers };
    if (body !== undefined && method !== 'GET') init.body = JSON.stringify(body);
    if (opts.keepalive) init.keepalive = true;
    var ctrl = null, timer = null;
    if (opts.timeoutMs && typeof AbortController === 'function') {
      ctrl = new AbortController();
      init.signal = ctrl.signal;
      timer = setTimeout(function () { try { ctrl.abort(); } catch (_) {} }, opts.timeoutMs);
    }
    return fetch(path, init).then(function (res) {
      if (timer) clearTimeout(timer);
      var ct = res.headers.get('content-type') || '';
      var isJson = ct.indexOf('application/json') !== -1;
      var parse = isJson ? res.json().catch(function () { return {}; }) : Promise.resolve({});
      return parse.then(function (data) {
        var r = { status: res.status, ok: res.ok, data: data || {}, network: false };
        if (!res.ok) reportApi(opts.area || 'account', path, method, { status: r.status, ok: false, data: r.data, nonJson: !isJson });
        return r;
      });
    }, function () {
      if (timer) clearTimeout(timer);
      reportApi(opts.area || 'account', path, method, { status: 0, ok: false, data: {}, network: true });
      return { status: 0, ok: false, data: {}, network: true };
    });
  }

  /* ---------------- tiny DOM builder (text only: server strings are never parsed as HTML) ---------------- */
  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] == null || attrs[k] === false) continue;
        if (k === 'text') n.textContent = attrs[k];
        else if (k === 'className') n.className = attrs[k];
        else if (k === 'onClick') n.addEventListener('click', attrs[k]);
        else if (k === 'hidden') n.hidden = !!attrs[k];
        else n.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
      }
    }
    (children || []).forEach(function (c) {
      if (c == null || c === false) return;
      n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return n;
  }
  function button(label, className, onClick, attrs) {
    var a = attrs || {};
    a.type = 'button';
    a.className = className;
    a.onClick = onClick;
    return el('button', a, [el('span', { className: 'btn-label', text: label })]);
  }
  /* The agreement sentence (D13.10; R12/R14/R31 agreementText). The canonical sentence gets the same links the baked
     pages carry (C3-K1: three); any other server wording is shown verbatim as text. */
  function agreement(text, className) {
    var t = typeof text === 'string' && text ? text : AGREEMENT;
    var p = el('p', { className: 'pay-agreement ' + (className || '') });
    if (t === AGREEMENT || t === AGREEMENT_PRE_K1) {
      p.appendChild(document.createTextNode('By continuing, you agree to our '));
      p.appendChild(el('a', { href: '/terms.html', text: 'Terms of Service' }));
      p.appendChild(document.createTextNode(t === AGREEMENT ? ', ' : ' and '));
      p.appendChild(el('a', { href: '/refund-policy.html', text: 'Refund Policy' }));
      if (t === AGREEMENT) {
        p.appendChild(document.createTextNode(' and '));
        p.appendChild(el('a', { href: '/affiliate-terms.html', text: 'Affiliate Program Terms' }));
      }
      p.appendChild(document.createTextNode('.'));
    } else {
      p.textContent = t;
    }
    return p;
  }

  function labelNode(btn) {
    var span = btn.querySelector('.btn-label');
    if (!span) { span = el('span', { className: 'btn-label' }); btn.appendChild(span); }
    return span;
  }
  function busy(btn, label) {
    if (!btn) return;
    var span = labelNode(btn);
    if (btn.dataset.idleLabel === undefined) btn.dataset.idleLabel = span.textContent;
    if (label) span.textContent = label;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-loading');
  }
  function idle(btn) {
    if (!btn) return;
    if (btn.dataset.idleLabel !== undefined) { labelNode(btn).textContent = btn.dataset.idleLabel; delete btn.dataset.idleLabel; }
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('is-loading');
  }
  /* A status line: kind = info | ok | warn | error. text is plain; link = {href, text} or {text, onClick}. */
  function notice(target, kind, text, link) {
    if (!target) return;
    target.textContent = '';
    target.className = 'cb-alert ' + (kind || 'info');
    target.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    if (!text) { target.hidden = true; return; }
    target.appendChild(document.createTextNode(text));
    if (link) {
      target.appendChild(document.createTextNode(' '));
      if (link.href) target.appendChild(el('a', { href: link.href, text: link.text, target: link.newTab ? '_blank' : null, rel: link.newTab ? 'noopener' : null }));
      else target.appendChild(el('button', { type: 'button', className: 'cb-linkbtn', text: link.text, onClick: link.onClick }));
    }
    target.hidden = false;
  }

  /* ---------------- the confirmation dialog ----------------
     opts: {
       action,                        event id (#39/#40 `action`)
       title,                         the dialog heading (ours)
       facts: [[label, value], …],    optional rows above the sentence
       texts: {confirmText, termsText, buttonLabel, agreementText} — the SERVER's words (or null with loadTexts)
       loadTexts: () => Promise<{texts, facts} | {error, link}>   e.g. R14's preview ("Checking price…" meanwhile)
       secondaryLabel,                LEGAL-COPY's second control ("Keep my trial", "Keep Monthly"); none = only x
       extra: {label, linkText, onLink}  the "Paying with Visa ••4242 · Use another card or Link" row
       run: (texts) => Promise<{ok:true, result} | {ok:false, message, link?, texts?, facts?, code?}>
       plain: true                    a confirmation that charges nothing (R41 Remove): no agreement sentence (it sits
                                      beside PAY buttons only, D13.10), a neutral error, no `dismissed` event
     }
     Resolves {ok:true, result} after a successful run, or {ok:false, dismissed:true}. */
  var openDialog = null;
  var MONEY_ERROR = 'That did not go through. Nothing has been charged. Please try again.';
  var PLAIN_ERROR = 'That did not go through. Please try again.';
  function confirmDialog(opts) {
    if (openDialog) return Promise.resolve({ ok: false, dismissed: true, busy: true });
    return new Promise(function (resolve) {
      var running = false, settled = false, texts = opts.texts || null, facts = opts.facts || [];
      var native = typeof window.HTMLDialogElement === 'function' && typeof document.createElement('dialog').showModal === 'function';
      var titleId = 'cb-d-title-' + Math.floor(Math.random() * 1e9);
      var confirmId = titleId + '-c';

      var box = el(native ? 'dialog' : 'div', {
        className: 'cb-dialog' + (native ? '' : ' cb-modal-box'),
        'aria-labelledby': titleId, 'aria-describedby': confirmId,
        role: native ? null : 'dialog', 'aria-modal': native ? null : 'true'
      });
      var x = el('button', { type: 'button', className: 'cb-x', 'aria-label': 'Close', text: '×' });
      var title = el('h2', { id: titleId, className: 'cb-d-title', tabindex: '-1', text: opts.title || '' });
      var factsBox = el('dl', { className: 'cb-facts' });
      var confirmP = el('p', { id: confirmId, className: 'cb-confirm' });
      var extraP = el('p', { className: 'cb-extra', hidden: true });
      var err = el('div', { className: 'cb-error', role: 'alert', hidden: true });
      var primary = button('', 'btn btn-primary cb-primary', null);
      var secondary = opts.secondaryLabel ? button(opts.secondaryLabel, 'btn btn-secondary cb-secondary', null) : null;
      var actions = el('div', { className: 'cb-d-actions' }, [primary, secondary]);
      var terms = el('p', { className: 'cb-terms', hidden: true });
      var agreeSlot = el('div', { className: 'cb-agree-slot' });
      [x, title, factsBox, confirmP, extraP, err, actions, terms, agreeSlot].forEach(function (n) { box.appendChild(n); });

      var overlay = null;
      if (!native) {
        overlay = el('div', { className: 'cb-modal' }, [box]);
        document.body.appendChild(overlay);
      } else {
        document.body.appendChild(box);
      }
      var lastFocus = document.activeElement;

      function renderFacts(list) {
        factsBox.textContent = '';
        (list || []).forEach(function (f) {
          if (!f || f[1] == null || f[1] === '') return;
          factsBox.appendChild(el('div', { className: 'cb-fact' }, [el('dt', { text: f[0] }), el('dd', { text: String(f[1]) })]));
        });
        factsBox.hidden = !factsBox.firstChild;
      }
      function renderTexts(t) {
        texts = t || texts;
        if (!texts) return;
        confirmP.textContent = texts.confirmText || texts.text || '';
        labelNode(primary).textContent = texts.buttonLabel || 'Continue';
        terms.textContent = texts.termsText || '';
        terms.hidden = !texts.termsText;
        agreeSlot.textContent = '';
        if (!opts.plain) agreeSlot.appendChild(agreement(texts.agreementText, 'cb-agreement'));
      }
      function renderExtra() {
        var e = opts.extra;
        if (!e || !e.label) { extraP.hidden = true; return; }
        extraP.textContent = e.label;
        if (e.linkText && typeof e.onLink === 'function') {
          extraP.appendChild(document.createTextNode(' · '));
          extraP.appendChild(el('button', { type: 'button', className: 'cb-linkbtn', text: e.linkText, onClick: function () {
            if (running) return;
            close({ ok: false, dismissed: true, via: 'use_another_method' });
            e.onLink();
          } }));
        }
        extraP.hidden = false;
      }
      function showError(msg, link) {
        notice(err, 'error', msg, link);
        err.className = 'cb-error';
      }
      function setRunning(on, label) {
        running = on;
        if (on) busy(primary, label || 'Working…'); else idle(primary);
        if (secondary) secondary.disabled = on;
        x.disabled = on;
      }
      function close(result) {
        if (settled) return;
        settled = true;
        openDialog = null;
        try { if (native && box.open) box.close(); } catch (_) {}
        (overlay || box).remove();
        document.removeEventListener('keydown', onKey, true);
        try { if (lastFocus && lastFocus.focus) lastFocus.focus(); } catch (_) {}
        if (result && result.dismissed && opts.action && !opts.plain) track('account_action_result', { action: opts.action, result: 'dismissed' });
        resolve(result);
      }
      function onKey(e) {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        if (!running) close({ ok: false, dismissed: true });
      }

      x.addEventListener('click', function () { if (!running) close({ ok: false, dismissed: true }); });
      if (secondary) secondary.addEventListener('click', function () { if (!running) close({ ok: false, dismissed: true }); });
      if (native) {
        box.addEventListener('cancel', function (e) { e.preventDefault(); if (!running) close({ ok: false, dismissed: true }); });
      }
      document.addEventListener('keydown', onKey, true);

      primary.addEventListener('click', function () {
        if (running || !texts) return;
        err.hidden = true;
        setRunning(true, 'Working…');
        Promise.resolve().then(function () { return opts.run(texts); }).then(function (r) {
          if (r && r.ok) { setRunning(false); close({ ok: true, result: r.result }); return; }
          setRunning(false);
          r = r || {};
          if (r.facts) renderFacts(r.facts);
          if (r.texts) renderTexts(r.texts);
          showError(r.message || (opts.plain ? PLAIN_ERROR : MONEY_ERROR), r.link || null);
          if (opts.action) track('account_action_result', { action: opts.action, result: r.result || 'error', code: r.code || undefined });
        }, function () {
          setRunning(false);
          showError(opts.plain ? PLAIN_ERROR : MONEY_ERROR);
        });
      });

      renderFacts(facts);
      renderExtra();
      if (texts) renderTexts(texts);
      openDialog = box;
      if (native) box.showModal();
      try { title.focus({ preventScroll: true }); } catch (_) { title.focus(); }

      if (!texts && typeof opts.loadTexts === 'function') {
        confirmP.textContent = 'Checking price…';
        primary.disabled = true;
        labelNode(primary).textContent = 'Checking price…';
        opts.loadTexts().then(function (r) {
          if (settled) return;
          primary.disabled = false;
          if (r && r.texts) { renderFacts(r.facts || facts); renderTexts(r.texts); return; }
          confirmP.textContent = '';
          primary.hidden = true;
          showError((r && r.message) || 'We couldn’t load the price. Nothing has been charged. Please try again in a moment.', r && r.link);
        });
      }
    });
  }

  /* ---------------- celebrations (D12.13; words = LEGAL-COPY §9.4 without the removed badge, D13.9) ---------------- */
  function planName(planID) { return planID === 'yearly' ? 'Yearly' : planID === 'monthly' ? 'Monthly' : ''; }
  function celebrationWords(c, ctx) {
    ctx = ctx || {};
    var plan = planName(c.planID) || ctx.planName || '';
    switch (c.kind) {
      case 'trial_started': return { h: 'Your free trial has started', l: ctx.trialEndMillis ? '3 interviews, up to 50 minutes each, until ' + day(ctx.trialEndMillis) + '.' : '' };
      case 'subscribed': return { h: 'Welcome to Interview Clozo' + (plan ? ' ' + plan : ''), l: 'Your plan is active.' };
      case 'trial_converted': return { h: 'Your ' + (plan || '') + (plan ? ' ' : '') + 'plan has started', l: 'Thanks for staying with us.' };
      case 'switched_yearly': return { h: 'You’re on Yearly', l: '' };
      case 'renewed_manual': return { h: 'Welcome back', l: plan ? 'Your ' + plan + ' plan is active again.' : '' };
      case 'renewed_auto': return { h: 'Thanks for renewing', l: plan && c.createdAtMillis ? 'Your ' + plan + ' plan renewed on ' + day(c.createdAtMillis) + '.' : '' };
      default: return null;
    }
  }
  var CHECK_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  function loadIgnition() {
    if (typeof window.clozoIgnition === 'function') return Promise.resolve(window.clozoIgnition);
    /* Only a src the bake stamped (the file exists) is loaded, so a page never requests a file that is not there. */
    var holder = document.querySelector('[data-celebrate-src]');
    var src = holder && holder.getAttribute('data-celebrate-src');
    if (!src || src.indexOf('?v=') === -1) return Promise.resolve(null);
    return new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { resolve(typeof window.clozoIgnition === 'function' ? window.clozoIgnition : null); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
  }
  /* c: {id, kind, planID, createdAtMillis}; opts: {slot, delayed, trialEndMillis, planName}. Returns true when shown. */
  function celebrate(c, opts) {
    opts = opts || {};
    var slot = opts.slot;
    var words = c && celebrationWords(c, opts);
    if (!slot || !words) return false;
    slot.textContent = '';
    var check = el('span', { className: 'cb-cel-check', 'aria-hidden': 'true' });
    check.innerHTML = CHECK_SVG;   // a constant, never server text
    var close = el('button', { type: 'button', className: 'cb-cel-x', 'aria-label': 'Close', text: '×' });
    slot.appendChild(el('div', { className: 'cb-cel-inner' }, [
      check,
      el('div', { className: 'cb-cel-words' }, [el('p', { className: 'cb-cel-h', text: words.h }), words.l ? el('p', { className: 'cb-cel-line', text: words.l }) : null]),
      close
    ]));
    slot.hidden = false;
    slot.classList.toggle('is-still', reducedMotion());
    // Force a frame so the entry transition runs (none under reduced motion).
    void slot.offsetWidth;
    slot.classList.add('is-in');
    var hideTimer = setTimeout(function () { hide(); }, 12000);
    function hide() {
      clearTimeout(hideTimer);
      slot.classList.remove('is-in');
      setTimeout(function () { slot.hidden = true; slot.textContent = ''; }, reducedMotion() ? 0 : 220);
    }
    close.addEventListener('click', hide);
    try {
      var seen = JSON.parse(localStorage.getItem('clozo_celebrated') || '[]');
      if (c.id && seen.indexOf(c.id) === -1) { seen.push(c.id); localStorage.setItem('clozo_celebrated', JSON.stringify(seen.slice(-20))); }
    } catch (_) {}
    track('celebration_shown', { kind: c.kind, delayed: !!opts.delayed });
    if (!reducedMotion()) {
      loadIgnition().then(function (fire) {
        if (!fire) return;
        try { fire({ kind: c.kind, size: 'card', id: c.id, slot: slot, delayed: !!opts.delayed }); } catch (_) {}
      });
    }
    return true;
  }
  function alreadyCelebrated(id) {
    try { return JSON.parse(localStorage.getItem('clozo_celebrated') || '[]').indexOf(id) !== -1; } catch (_) { return false; }
  }

  /* ---------------- in-place actions (checkout.js → 409 use_in_place_action) ---------------- */
  function openAction(action, opts) {
    if (typeof actionHandler !== 'function') return false;
    try { return actionHandler(action, opts || {}) === true; } catch (_) { return false; }
  }
  function setActionHandler(fn) { actionHandler = typeof fn === 'function' ? fn : null; }

  /* ---------------- an email address on the page (FIX4-SITE #2) ----------------
     Never broken mid-word: it may wrap only before "@" and after each "." or "-" (<wbr> between pieces). Each piece is a
     nowrap .cb-email-part (billing.css), so a piece wider than the line ends in an ellipsis instead of splitting or
     running off the screen; the full address stays in the text (copy, screen readers) and in title. A facts row
     (.cb-fact / .account-meta-row) holding one gets .has-email, which moves the address under its label when the two
     don't fit side by side. */
  function emailSpan(value) {
    var s = document.createElement('span');
    s.className = 'cb-email';
    s.title = value;
    String(value).replace(/@/g, '\n@').replace(/([.-])/g, '$1\n').split('\n').forEach(function (part, i) {   // no lookbehind (old Safari)
      if (!part) return;
      if (i) s.appendChild(document.createElement('wbr'));
      var p = document.createElement('span');
      p.className = 'cb-email-part';
      p.textContent = part;
      s.appendChild(p);
    });
    return s;
  }
  function email(node, value) {
    if (!node) return;
    var row = node.closest && node.closest('.cb-fact, .account-meta-row');
    if (row) row.classList.toggle('has-email', !!value && value.indexOf('@') > 0);
    node.textContent = '';
    if (value) node.appendChild(emailSpan(value));
  }
  /* text with the address inside it (e.g. "We've emailed a confirmation to {email}.") — same wrapping rules. */
  function emailText(node, text, value) {
    if (!node) return;
    var at = value ? String(text).indexOf(value) : -1;
    if (at < 0) { node.textContent = text; return; }
    node.textContent = String(text).slice(0, at);
    node.appendChild(emailSpan(value));
    node.appendChild(document.createTextNode(String(text).slice(at + value.length)));
  }

  window.clozoBillingUI = {
    version: 1,
    AGREEMENT: AGREEMENT,
    money: money, per: per, day: day, shortDay: shortDay, stamp: stamp, timeZone: timeZone,
    uuid: uuid, isMac: isMac, reducedMotion: reducedMotion, token: token, appHandoff: appHandoff,
    track: track, api: api, el: el, button: button, agreement: agreement,
    busy: busy, idle: idle, notice: notice,
    confirm: confirmDialog, isDialogOpen: function () { return !!openDialog; },
    celebrate: celebrate, celebrationWords: celebrationWords, alreadyCelebrated: alreadyCelebrated,
    openAction: openAction, setActionHandler: setActionHandler,
    email: email, emailText: emailText
  };
})();
